import { useState } from 'react'
import { axiosPost } from '../../../../modules/axiosHelper'
import {
  cancel,
  n_lesson,
  n_upcoming_lessons,
  n_weekly,
  s_common,
  s_lesson_edit,
  s_upcoming_lessons,
} from '../../../../modules/i18n/strings/auto/wording'

import { BUTTON_SIZE, BUTTON_TYPE, DSButton, DSRadio } from '@repo/design-system/index'
import { QIcon_book } from '@repo/icons/index'
import { useQueryClient } from '@tanstack/react-query'
import Parser from 'react-html-parser'
import apis from '../../../../modules/apis'
import { useCurrentUser } from '../../../../modules/context/UserContext'
import useIsMobile from '../../../../modules/hooks/common/useIsMobile'
import useIsWebview from '../../../../modules/hooks/common/useIsWebview'
import useTranslation from '../../../../modules/i18n/useTranslation'
import { UpcomingLesson } from '../../../../modules/lessonTypes'
import { MATCHING_AFTER } from '../../../../modules/vars'
import { useBasicPopup } from '../../../basic/popup/BasicPopup'
import CreditBadge from '../../../common/buttons/CreditBadge'
import { CardTextarea } from '../../materials/QuestionSelect/AnswerItem'
import TutorAvatar from '../TutorAvatar'

interface CancelModalBodyProps {
  message: {
    title: string
    content: string
    allowed: boolean
  }
  item: UpcomingLesson
  cancelList: {
    value: string
    name: string
  }[]
  handleCancelWebview?: (item: UpcomingLesson, reason: string, reasonDetail: string) => Promise<{ success: boolean }>
  isWeeklyLessonPackage?: boolean
  timeDiff?: number
}

const CancelModalBody = ({
  message,
  item,
  cancelList,
  isWeeklyLessonPackage,
  timeDiff,
  handleCancelWebview,
}: CancelModalBodyProps) => {
  const basicPopup = useBasicPopup()
  const queryClient = useQueryClient()
  const { currentUser } = useCurrentUser()
  const isMobile = useIsMobile()
  const isWebview = useIsWebview()

  const [reason, setReason] = useState()
  const [reasonDetail, setReasonDetail] = useState('')
  const [loading, setLoading] = useState(false)

  const onSuccessCancel = async (message: string) => {
    await queryClient.invalidateQueries([apis.lessons.upcomingsV5()])
    await queryClient.invalidateQueries([apis.lessons.upcomingsCount()])
    basicPopup.close('cancel-popup')
    basicPopup.show({
      id: 'cancel-result-popup',
      maxW: 600,
      bodyPb: 24,
      title: t(s_upcoming_lessons.lesson_cancellation),
      body: (
        <>
          <div className="flex w-full flex-col">
            <div className="mt-[16px] text-[14px]">{message}</div>
            <div className="mt-[40px] flex w-full justify-end">
              <DSButton
                size={BUTTON_SIZE.LG}
                type={BUTTON_TYPE.SOLID}
                onClick={() => basicPopup.close('cancel-result-popup')}
              >
                <DSButton.Label>{t(s_common.confirm)}</DSButton.Label>
              </DSButton>
            </div>
          </div>
        </>
      ),
    })
  }

  const handleCancel = async () => {
    setLoading(true)

    const payload = {
      cancel_reason_type: reason,
      cancel_reason_text: reasonDetail,
    }

    const response = await axiosPost(apis.lesson.cancel(item.id), payload)
    console.log('response', response)

    if (response.success) {
      //good!
      onSuccessCancel(response.message)
      // setLoading(false)
    } else {
      setLoading(false)
    }
  }

  const { t } = useTranslation()
  const courseTitle = item.course?.title ? item.course?.title : item.course_title

  return (
    <div className="px-[16px]">
      <div className="mt-[8px] mb-[0px] h-auto rounded-[10px] border-[1.5px] border-gray-200 p-[16px]">
        <div className="mx-[0px] mb-[8px] flex items-start rounded-[10px]">
          <div className="flex items-center gap-12">
            <div className="">
              {/** style hard coding */}
              <CreditBadge
                duration={item.duration}
                style={{
                  paddingTop: '6px',
                  paddingBottom: '6px',
                  paddingLeft: '8px',
                  paddingRight: '8px',
                  color: '#140F33',
                  backgroundColor: 'rgb(228, 231, 244)',
                  fontWeight: 'bold',
                  height: '30px',
                }}
              />
            </div>
            <div className="mx-[0px] flex items-center justify-center rounded-lg leading-6">
              {item.tutor_canceled && item.matching_after_option == MATCHING_AFTER.SHOW_AVAILABLE_TIMES ? (
                <div className="sm:p-0">
                  <span className="mb-[0px] mr-[3px] h-[30px] text-[20px] font-bold text-gray-300 line-through">
                    {item.formatted_start_time}
                  </span>
                </div>
              ) : (
                <div className="sm:p-0">
                  <span className="mb-[0px] mr-[3px] h-[30px] text-[20px] font-bold text-black">
                    {item.formatted_start_time}
                  </span>
                </div>
              )}
              <div className="text-sm font-medium text-gray-500 hidden sm:block">{currentUser?.timezone}</div>
            </div>
          </div>
        </div>
        <div className="text-sm font-medium text-gray-500 sm:hidden">{currentUser?.timezone}</div>
        <div className="mx-[0px] mb-[24px] flex-col items-center rounded-lg">
          <TutorAvatar tutor={item.tutor} />
        </div>
        <div className="mx-[0px] flex-col items-center rounded-lg">
          <div className="flex-1">
            <div className="mx-[0px] mt-[8px] items-center rounded-lg">
              <span className="flex text-[14px] text-gray-700">
                <QIcon_book width="20px" height="20px" />
                <span className="ml-[4px] font-bold text-black">
                  {item.material_id ? `${courseTitle}` : t(n_lesson.msg_material_auto3)}
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-[23px] mb-[25px] w-full rounded-md bg-gray-50">
        <div className="flex flex-col px-[16px] py-[16px] text-[14px] font-normal">
          <div className="text-[14px] font-medium text-red-500">[{Parser(message.title)}]</div>
          <div className="font-medium">{Parser(message.content)}</div>
        </div>
      </div>

      {isWeeklyLessonPackage && (
        <div className="mt-[23px] mb-[25px] w-full rounded-md bg-gray-50">
          <div className="flex flex-col px-[16px] py-[16px] text-[14px] font-normal">
            <div className="text-[14px] font-medium text-red-500">{t(n_weekly.cancel.title)}</div>
            <div className="font-medium">{timeDiff > 24 ? t(cancel.weekly_prior_24) : t(cancel.weekly_prior_24_2)}</div>
          </div>
        </div>
      )}

      <div className="mb-[10px] text-[16px] font-medium">{t(s_lesson_edit.select_the_reason_for_cancellation)}</div>
      <div className="text-[14px] w-full">
        <DSRadio controlSelected={[reason, setReason]} containerClassName="flex flex-col space-y-[8px] w-full">
          {cancelList.map((cancelItem, idx) => {
            return (
              <div key={`radio-wrapper-${idx}`} className="w-full max-w-full overflow-hidden">
                <DSRadio.Item
                  key={`cancle-radio-group1-${idx}`}
                  value={cancelItem.value}
                  className="!flex !flex-row !items-start !w-full !max-w-full"
                >
                  <span className="flex-1 break-words whitespace-normal word-break overflow-wrap-anywhere min-w-0 pr-2">
                    {cancelItem.name}
                  </span>
                </DSRadio.Item>
              </div>
            )
          })}
        </DSRadio>
      </div>
      <div>
        {reason === 4 && (
          <CardTextarea
            style={{ resize: 'none' }}
            className="{ mt-4 flex w-full rounded"
            rows={5}
            placeholder={t(s_lesson_edit.let_us_know_the_reason_for_cancelling)}
            onChange={(e) => {
              setReasonDetail(e.target.value)
            }}
          />
        )}
      </div>
      <div className="mt-[32px] flex justify-end text-center">
        {reason ? (
          <>
            <DSButton
              type={BUTTON_TYPE.SOLID}
              size={BUTTON_SIZE.LG}
              isLoading={loading}
              disabled={loading}
              onClick={
                isWebview
                  ? async () => {
                      setLoading(true)
                      const result = await handleCancelWebview(item, reason, reasonDetail)
                      // Only set loading to false if cancellation failed
                      if (!result?.success) {
                        setLoading(false)
                      }
                      // If successful, keep loading true (user will be redirected)
                    }
                  : handleCancel
              }
              className="w-fit text-white"
            >
              <DSButton.Label>{t(s_lesson_edit.request_cancellation)}</DSButton.Label>
            </DSButton>
          </>
        ) : (
          <DSButton
            type={BUTTON_TYPE.SOLID}
            size={BUTTON_SIZE.LG}
            onClick={() => console.log('수업 취소 불가')}
            disabled
            className="text-white"
          >
            {t(n_upcoming_lessons.lesson_cancel.reason_desc)}
          </DSButton>
        )}
      </div>
    </div>
  )
}

export default CancelModalBody
